/**
 * Single entry point for all catering action creators. Fully inlined.
 */
import { CL, CE, CO, CC, CI, COT, CS, CCT, CT, CRT, CATERING_LOCATION_CHANGED } from './cateringTypes';
import type {
  FetchLeadsPayload,
  FetchLeadsBoardPayload,
  FetchBoardColumnPayload,
  MappedBoardColumn,
  FetchAnalyticsPayload,
  LeadsAnalytics,
  CreateLeadPayload,
  UpdateLeadPayload,
  StatusRequestPayload,
  ConvertLeadPayload,
  FetchOrdersPayload,
  CreateOrderPayload,
  UpdateOrderPayload,
  OrderActionPayload,
  TransitionStatusPayload,
  SendOrderData,
  OrderDayCapacity,
  FetchCustomersPayload,
  FetchCustomerDetailPayload,
  UpdateCustomerPayload,
  DeleteCustomerPayload,
  CateringOrderType,
  CateringOrderTypeFeeRedux,
  FetchCommTemplatesPayload,
  UpsertCommTemplateRequestPayload,
  RestoreCommTemplateRequestPayload,
  TaxItem,
  FetchTaxesRequestAction,
  FetchTaxesSuccessAction,
  FetchTaxesFailureAction,
  FetchResolvedTemplatePayload,
  ResolvedNotificationTemplate,
} from './cateringTypes';
import type {
  CateringLead,
  LeadStatus,
  CateringEstimate,
  EstimateLineItem,
  AppliedCharge,
  AppliedTax,
  OrderDiscount,
  CateringOrder,
  OrderStatus,
  CateringCustomer,
  CateringInvoice,
  InvoiceStatus,
  Payment,
  RefundReason,
  CommunicationLogEntry,
  LeadAddress,
} from '../../components/catering/data/types';
import type { LeadsPageMeta, CatalogItem, PageMeta, ApiCommTemplate, TemplateTag, SendOutcome } from '../../services/cateringApi';

// ════════════════════════════════════════════════════════════════════════════════
// LEADS
// ════════════════════════════════════════════════════════════════════════════════

export const fetchLeadsRequest = (payload: FetchLeadsPayload = {}) =>
  ({ type: CL.FETCH_REQUEST, payload } as const);

export const fetchLeadDetailRequest = (leadId: string) =>
  ({ type: CL.FETCH_DETAIL_REQUEST, payload: { leadId } } as const);

export const fetchLeadDetailSuccess = (lead: CateringLead) =>
  ({ type: CL.FETCH_DETAIL_SUCCESS, payload: { lead } } as const);

export const fetchLeadDetailFailure = (error: string) =>
  ({ type: CL.FETCH_DETAIL_FAILURE, payload: { error } } as const);

export const fetchLeadsSuccess = (leads: CateringLead[], locationId: string, page: LeadsPageMeta) =>
  ({ type: CL.FETCH_SUCCESS, payload: { leads, locationId, page } } as const);

export const fetchLeadsFailure = (error: string) =>
  ({ type: CL.FETCH_FAILURE, payload: { error } } as const);

// ── Linkable leads (Estimate/Order "Link to Lead" dropdown — dedicated /linkable endpoint) ──
export const fetchLinkableLeadsRequest = (payload: FetchLeadsPayload = {}) =>
  ({ type: CL.LINKABLE_FETCH_REQUEST, payload } as const);

export const fetchLinkableLeadsSuccess = (leads: CateringLead[], locationId: string) =>
  ({ type: CL.LINKABLE_FETCH_SUCCESS, payload: { leads, locationId } } as const);

export const fetchLinkableLeadsFailure = (error: string) =>
  ({ type: CL.LINKABLE_FETCH_FAILURE, payload: { error } } as const);

export const createLeadRequest = (payload: CreateLeadPayload) =>
  ({ type: CL.CREATE_REQUEST, payload } as const);

export const createLeadSuccess = (lead: CateringLead) =>
  ({ type: CL.CREATE_SUCCESS, payload: { lead } } as const);

export const createLeadFailure = (error: string) =>
  ({ type: CL.CREATE_FAILURE, payload: { error } } as const);

export const updateLeadRequest = (payload: UpdateLeadPayload) =>
  ({ type: CL.UPDATE_REQUEST, payload } as const);

export const updateLeadSuccess = (leadId: string, changes: Partial<CateringLead>) =>
  ({ type: CL.UPDATE_SUCCESS, payload: { leadId, changes } } as const);

export const updateLeadFailure = (error: string) =>
  ({ type: CL.UPDATE_FAILURE, payload: { error } } as const);

export const transitionStatusRequest = (payload: StatusRequestPayload) =>
  ({ type: CL.STATUS_REQUEST, payload } as const);

export const transitionStatusSuccess = (leadId: string, status: LeadStatus, prevStatus: LeadStatus) =>
  ({ type: CL.STATUS_SUCCESS, payload: { leadId, status, prevStatus } } as const);

export const transitionStatusFailure = (error: string, leadId: string, prevStatus: LeadStatus) =>
  ({ type: CL.STATUS_FAILURE, payload: { error, leadId, prevStatus } } as const);

export const convertLeadRequest = (payload: ConvertLeadPayload) =>
  ({ type: CL.CONVERT_REQUEST, payload } as const);

export const convertLeadSuccess = (leadId: string, orderId: string) =>
  ({ type: CL.CONVERT_SUCCESS, payload: { leadId, orderId } } as const);

export const convertLeadFailure = (error: string) =>
  ({ type: CL.CONVERT_FAILURE, payload: { error } } as const);

export const deleteLeadRequest = (leadId: string) =>
  ({ type: CL.DELETE_REQUEST, payload: { leadId } } as const);

export const deleteLeadSuccess = (leadId: string) =>
  ({ type: CL.DELETE_SUCCESS, payload: { leadId } } as const);

export const deleteLeadFailure = (error: string) =>
  ({ type: CL.DELETE_FAILURE, payload: { error } } as const);

export const deleteLeadLocal = (leadId: string) =>
  ({ type: CL.DELETE_LOCAL, payload: { leadId } } as const);

export const optimisticUpdateLead = (leadId: string, changes: Partial<CateringLead>) =>
  ({ type: CL.OPTIMISTIC_UPDATE, payload: { leadId, changes } } as const);

export const clearConvertedOrderId = () =>
  ({ type: CL.CLEAR_CONVERTED } as const);

export const clearLeadStatusFeedback = () =>
  ({ type: CL.CLEAR_STATUS_FEEDBACK } as const);

export const fetchAnalyticsRequest = (payload: FetchAnalyticsPayload = {}) =>
  ({ type: CL.FETCH_ANALYTICS_REQUEST, payload } as const);

export const fetchAnalyticsSuccess = (analytics: LeadsAnalytics) =>
  ({ type: CL.FETCH_ANALYTICS_SUCCESS, payload: { analytics } } as const);

export const fetchAnalyticsFailure = (error: string) =>
  ({ type: CL.FETCH_ANALYTICS_FAILURE, payload: { error } } as const);

// ── Kanban board (per-column pagination) ──────────────────────────────────────

export const fetchLeadsBoardRequest = (payload: FetchLeadsBoardPayload) =>
  ({ type: CL.BOARD_FETCH_REQUEST, payload } as const);

export const fetchLeadsBoardSuccess = (columns: MappedBoardColumn[], locationId: string, filterKey: string) =>
  ({ type: CL.BOARD_FETCH_SUCCESS, payload: { columns, locationId, filterKey } } as const);

export const fetchLeadsBoardFailure = (error: string) =>
  ({ type: CL.BOARD_FETCH_FAILURE, payload: { error } } as const);

export const fetchBoardColumnRequest = (payload: FetchBoardColumnPayload) =>
  ({ type: CL.BOARD_COLUMN_REQUEST, payload } as const);

export const fetchBoardColumnSuccess = (
  status: LeadStatus, items: CateringLead[], page: number, total: number, hasMore: boolean,
) =>
  ({ type: CL.BOARD_COLUMN_SUCCESS, payload: { status, items, page, total, hasMore } } as const);

export const fetchBoardColumnFailure = (status: LeadStatus, error: string) =>
  ({ type: CL.BOARD_COLUMN_FAILURE, payload: { status, error } } as const);

export type CateringLeadsAction =
  | ReturnType<typeof fetchLeadsRequest>
  | ReturnType<typeof fetchLeadsSuccess>
  | ReturnType<typeof fetchLeadsFailure>
  | ReturnType<typeof fetchLinkableLeadsRequest>
  | ReturnType<typeof fetchLinkableLeadsSuccess>
  | ReturnType<typeof fetchLinkableLeadsFailure>
  | ReturnType<typeof fetchLeadDetailRequest>
  | ReturnType<typeof fetchLeadDetailSuccess>
  | ReturnType<typeof fetchLeadDetailFailure>
  | ReturnType<typeof createLeadRequest>
  | ReturnType<typeof createLeadSuccess>
  | ReturnType<typeof createLeadFailure>
  | ReturnType<typeof updateLeadRequest>
  | ReturnType<typeof updateLeadSuccess>
  | ReturnType<typeof updateLeadFailure>
  | ReturnType<typeof transitionStatusRequest>
  | ReturnType<typeof transitionStatusSuccess>
  | ReturnType<typeof transitionStatusFailure>
  | ReturnType<typeof convertLeadRequest>
  | ReturnType<typeof convertLeadSuccess>
  | ReturnType<typeof convertLeadFailure>
  | ReturnType<typeof deleteLeadRequest>
  | ReturnType<typeof deleteLeadSuccess>
  | ReturnType<typeof deleteLeadFailure>
  | ReturnType<typeof deleteLeadLocal>
  | ReturnType<typeof optimisticUpdateLead>
  | ReturnType<typeof clearConvertedOrderId>
  | ReturnType<typeof clearLeadStatusFeedback>
  | ReturnType<typeof fetchAnalyticsRequest>
  | ReturnType<typeof fetchAnalyticsSuccess>
  | ReturnType<typeof fetchAnalyticsFailure>
  | ReturnType<typeof fetchLeadsBoardRequest>
  | ReturnType<typeof fetchLeadsBoardSuccess>
  | ReturnType<typeof fetchLeadsBoardFailure>
  | ReturnType<typeof fetchBoardColumnRequest>
  | ReturnType<typeof fetchBoardColumnSuccess>
  | ReturnType<typeof fetchBoardColumnFailure>;

// ════════════════════════════════════════════════════════════════════════════════
// ESTIMATES
// ════════════════════════════════════════════════════════════════════════════════

export interface FetchEstimatesParams {
  locationId?: string;
  token?: string | null;
  status?: string;
  query?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const fetchEstimatesRequest = (payload: FetchEstimatesParams = {}) =>
  ({ type: CE.FETCH_REQUEST, payload } as const);

export const fetchEstimatesSuccess = (estimates: CateringEstimate[], locationId: string, page: PageMeta) =>
  ({ type: CE.FETCH_SUCCESS, payload: { estimates, locationId, page } } as const);

export const fetchEstimatesFailure = (error: string) =>
  ({ type: CE.FETCH_FAILURE, payload: { error } } as const);

export const deleteEstimateLocal = (estimateId: string) =>
  ({ type: CE.DELETE_LOCAL, payload: { estimateId } } as const);

export const deleteEstimateRequest = (estimateId: string, locationId?: string, token?: string | null) =>
  ({ type: CE.DELETE_REQUEST, payload: { estimateId, locationId, token } } as const);

export const deleteEstimateSuccess = (estimateId: string) =>
  ({ type: CE.DELETE_SUCCESS, payload: { estimateId } } as const);

export const deleteEstimateFailure = (error: string) =>
  ({ type: CE.DELETE_FAILURE, payload: { error } } as const);

export interface CreateEstimateFormData {
  leadId: string | null;
  eventDate: string;
  /** "HH:MM" 24h, or '' when the coordinator left it blank — optional since the
   *  backend accepts a missing/null event time. The saga converts '' to null on the wire. */
  eventTime: string;
  dispatchTime?: string;
  servingTime?: string;
  /** India locations only. Scoped to this estimate — not the customer/lead profile. */
  gstin?: string;
  orderTypeId: string;
  orderType: string;  // display name, for local state
  headcount: number;
  validUntil: string;
  lineItems: EstimateLineItem[];
  discount: OrderDiscount;
  appliedCharges: AppliedCharge[];
  appliedTaxes?: AppliedTax[];
  // passthrough to local state after success
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  company: string;
  address?: LeadAddress;
  eventName: string;
  eventType: string;
  eventTypeOther: string;
  taxExempt: boolean;
  taxRate: number;
  taxAmount: number;
  serviceCharge: number;
  subtotal: number;
  total: number;
  contractTerms?: string;
  instructions?: string;
  notes?: string;
  dietaryRestrictions?: string[];
  sendImmediately?: boolean;
}

export const createEstimateRequest = (data: CreateEstimateFormData, locationId?: string, token?: string | null) =>
  ({ type: CE.CREATE_REQUEST, payload: { data, locationId, token } } as const);

export const createEstimateSuccess = (estimate: CateringEstimate) =>
  ({ type: CE.CREATE_SUCCESS, payload: { estimate } } as const);

export const createEstimateFailure = (error: string) =>
  ({ type: CE.CREATE_FAILURE, payload: { error } } as const);

export const updateEstimateRequest = (id: string, data: Partial<CreateEstimateFormData>, locationId?: string, token?: string | null) =>
  ({ type: CE.UPDATE_REQUEST, payload: { id, data, locationId, token } } as const);

export const updateEstimateSuccess = (estimate: CateringEstimate) =>
  ({ type: CE.UPDATE_SUCCESS, payload: { estimate } } as const);

export const updateEstimateFailure = (error: string) =>
  ({ type: CE.UPDATE_FAILURE, payload: { error } } as const);

export const fetchCatalogItemsRequest = () =>
  ({ type: CE.FETCH_CATALOG_ITEMS_REQUEST } as const);

export const fetchCatalogItemsSuccess = (items: CatalogItem[]) =>
  ({ type: CE.FETCH_CATALOG_ITEMS_SUCCESS, payload: { items } } as const);

export const fetchCatalogItemsFailure = (error: string) =>
  ({ type: CE.FETCH_CATALOG_ITEMS_FAILURE, payload: { error } } as const);

export const fetchEstimateDetailRequest = (id: string) =>
  ({ type: CE.FETCH_DETAIL_REQUEST, payload: { id } } as const);

export const fetchEstimateDetailSuccess = (estimate: CateringEstimate) =>
  ({ type: CE.FETCH_DETAIL_SUCCESS, payload: { estimate } } as const);

export const fetchEstimateDetailFailure = (error: string) =>
  ({ type: CE.FETCH_DETAIL_FAILURE, payload: { error } } as const);

export interface SendEstimateData {
  channels: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  smsBodyOverride?: string;
  phoneOverride?: string;
}

export const sendEstimateRequest = (id: string, data: SendEstimateData) =>
  ({ type: CE.SEND_REQUEST, payload: { id, data } } as const);

export const sendEstimateSuccess = (
  estimateId: string,
  result: SendOutcome & { status: string; sentAt: string | null },
) => ({ type: CE.SEND_SUCCESS, payload: { estimateId, ...result } } as const);

export const sendEstimateFailure = (error: string) =>
  ({ type: CE.SEND_FAILURE, payload: { error } } as const);

export const updateEstimateStatusRequest = (id: string, action: 'approve' | 'reject') =>
  ({ type: CE.UPDATE_STATUS_REQUEST, payload: { id, action } } as const);

export const updateEstimateStatusSuccess = (estimateId: string, status: string, updatedAt: string) =>
  ({ type: CE.UPDATE_STATUS_SUCCESS, payload: { estimateId, status, updatedAt } } as const);

export const updateEstimateStatusFailure = (error: string) =>
  ({ type: CE.UPDATE_STATUS_FAILURE, payload: { error } } as const);

export type CateringEstimatesAction =
  | ReturnType<typeof fetchEstimatesRequest>
  | ReturnType<typeof fetchEstimatesSuccess>
  | ReturnType<typeof fetchEstimatesFailure>
  | ReturnType<typeof deleteEstimateLocal>
  | ReturnType<typeof deleteEstimateRequest>
  | ReturnType<typeof deleteEstimateSuccess>
  | ReturnType<typeof deleteEstimateFailure>
  | ReturnType<typeof createEstimateRequest>
  | ReturnType<typeof createEstimateSuccess>
  | ReturnType<typeof createEstimateFailure>
  | ReturnType<typeof updateEstimateRequest>
  | ReturnType<typeof updateEstimateSuccess>
  | ReturnType<typeof updateEstimateFailure>
  | ReturnType<typeof fetchCatalogItemsRequest>
  | ReturnType<typeof fetchCatalogItemsSuccess>
  | ReturnType<typeof fetchCatalogItemsFailure>
  | ReturnType<typeof fetchEstimateDetailRequest>
  | ReturnType<typeof fetchEstimateDetailSuccess>
  | ReturnType<typeof fetchEstimateDetailFailure>
  | ReturnType<typeof sendEstimateRequest>
  | ReturnType<typeof sendEstimateSuccess>
  | ReturnType<typeof sendEstimateFailure>
  | ReturnType<typeof updateEstimateStatusRequest>
  | ReturnType<typeof updateEstimateStatusSuccess>
  | ReturnType<typeof updateEstimateStatusFailure>;

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS
// ════════════════════════════════════════════════════════════════════════════════

export const fetchOrdersRequest = (payload: FetchOrdersPayload = {}) =>
  ({ type: CO.FETCH_REQUEST, payload } as const);

export const fetchOrdersSuccess = (orders: CateringOrder[], locationId: string, total: number, page?: PageMeta) =>
  ({ type: CO.FETCH_SUCCESS, payload: { orders, locationId, total, page } } as const);

export const fetchOrdersFailure = (error: string) =>
  ({ type: CO.FETCH_FAILURE, payload: { error } } as const);

export const fetchOrderDetailRequest = (orderId: string, locationId?: string) =>
  ({ type: CO.FETCH_DETAIL_REQUEST, payload: { orderId, locationId } } as const);

export const fetchOrderDetailSuccess = (order: CateringOrder) =>
  ({ type: CO.FETCH_DETAIL_SUCCESS, payload: { order } } as const);

export const fetchOrderDetailFailure = (error: string) =>
  ({ type: CO.FETCH_DETAIL_FAILURE, payload: { error } } as const);

export const createOrderRequest = (payload: CreateOrderPayload) =>
  ({ type: CO.CREATE_REQUEST, payload } as const);

export const createOrderSuccess = (order: CateringOrder) =>
  ({ type: CO.CREATE_SUCCESS, payload: { order } } as const);

export const createOrderFailure = (error: string) =>
  ({ type: CO.CREATE_FAILURE, payload: { error } } as const);

export const updateOrderRequest = (payload: UpdateOrderPayload) =>
  ({ type: CO.UPDATE_REQUEST, payload } as const);

export const updateOrderSuccess = (order: CateringOrder) =>
  ({ type: CO.UPDATE_SUCCESS, payload: { order } } as const);

export const updateOrderWarning = (warning: { code: string; message: string; requiresConfirmation: boolean }) =>
  ({ type: CO.UPDATE_WARNING, payload: { warning } } as const);

export const updateOrderFailure = (error: string) =>
  ({ type: CO.UPDATE_FAILURE, payload: { error } } as const);

export const orderActionRequest = (payload: OrderActionPayload) =>
  ({ type: CO.ACTION_REQUEST, payload } as const);

export const orderActionSuccess = (order: CateringOrder) =>
  ({ type: CO.ACTION_SUCCESS, payload: { order } } as const);

export const orderActionFailure = (error: string) =>
  ({ type: CO.ACTION_FAILURE, payload: { error } } as const);

export const transitionOrderStatusRequest = (payload: TransitionStatusPayload) =>
  ({ type: CO.STATUS_REQUEST, payload } as const);

export const transitionOrderStatusSuccess = (orderId: string, status: OrderStatus) =>
  ({ type: CO.STATUS_SUCCESS, payload: { orderId, status } } as const);

export const transitionOrderStatusFailure = (error: string) =>
  ({ type: CO.STATUS_FAILURE, payload: { error } } as const);

export const sendOrderRequest = (orderId: string, data: SendOrderData) =>
  ({ type: CO.SEND_REQUEST, payload: { orderId, data } } as const);

export const sendOrderSuccess = (orderId: string, sentAt: string) =>
  ({ type: CO.SEND_SUCCESS, payload: { orderId, sentAt } } as const);

export const sendOrderFailure = (error: string) =>
  ({ type: CO.SEND_FAILURE, payload: { error } } as const);

export const fetchOrderDayCapacityRequest = (date: string, excludeOrderId?: string) =>
  ({ type: CO.DAY_CAPACITY_REQUEST, payload: { date, excludeOrderId } } as const);

export const fetchOrderDayCapacitySuccess = (capacity: OrderDayCapacity) =>
  ({ type: CO.DAY_CAPACITY_SUCCESS, payload: { capacity } } as const);

export const fetchOrderDayCapacityFailure = (error: string) =>
  ({ type: CO.DAY_CAPACITY_FAILURE, payload: { error } } as const);

export type CateringOrdersAction =
  | ReturnType<typeof fetchOrdersRequest>
  | ReturnType<typeof fetchOrdersSuccess>
  | ReturnType<typeof fetchOrdersFailure>
  | ReturnType<typeof fetchOrderDetailRequest>
  | ReturnType<typeof fetchOrderDetailSuccess>
  | ReturnType<typeof fetchOrderDetailFailure>
  | ReturnType<typeof createOrderRequest>
  | ReturnType<typeof createOrderSuccess>
  | ReturnType<typeof createOrderFailure>
  | ReturnType<typeof updateOrderRequest>
  | ReturnType<typeof updateOrderSuccess>
  | ReturnType<typeof updateOrderWarning>
  | ReturnType<typeof updateOrderFailure>
  | ReturnType<typeof orderActionRequest>
  | ReturnType<typeof orderActionSuccess>
  | ReturnType<typeof orderActionFailure>
  | ReturnType<typeof transitionOrderStatusRequest>
  | ReturnType<typeof transitionOrderStatusSuccess>
  | ReturnType<typeof transitionOrderStatusFailure>
  | ReturnType<typeof sendOrderRequest>
  | ReturnType<typeof sendOrderSuccess>
  | ReturnType<typeof sendOrderFailure>
  | ReturnType<typeof fetchOrderDayCapacityRequest>
  | ReturnType<typeof fetchOrderDayCapacitySuccess>
  | ReturnType<typeof fetchOrderDayCapacityFailure>;

// ════════════════════════════════════════════════════════════════════════════════
// CUSTOMERS
// ════════════════════════════════════════════════════════════════════════════════

export const fetchCustomersRequest = (payload: FetchCustomersPayload = {}) =>
  ({ type: CC.FETCH_REQUEST, payload } as const);

export const fetchCustomersSuccess = (customers: CateringCustomer[], total: number, page?: PageMeta) =>
  ({ type: CC.FETCH_SUCCESS, payload: { customers, total, page } } as const);

export const fetchCustomersFailure = (error: string) =>
  ({ type: CC.FETCH_FAILURE, payload: { error } } as const);

export const fetchCustomerDetailRequest = (payload: FetchCustomerDetailPayload) =>
  ({ type: CC.FETCH_DETAIL_REQUEST, payload } as const);

export const fetchCustomerDetailSuccess = (customer: CateringCustomer) =>
  ({ type: CC.FETCH_DETAIL_SUCCESS, payload: { customer } } as const);

export const fetchCustomerDetailFailure = (error: string) =>
  ({ type: CC.FETCH_DETAIL_FAILURE, payload: { error } } as const);

export const updateCustomerRequest = (payload: UpdateCustomerPayload) =>
  ({ type: CC.UPDATE_REQUEST, payload } as const);

export const updateCustomerSuccess = (customerId: string, patch: UpdateCustomerPayload['data']) =>
  ({ type: CC.UPDATE_SUCCESS, payload: { customerId, patch } } as const);

export const updateCustomerFailure = (error: string) =>
  ({ type: CC.UPDATE_FAILURE, payload: { error } } as const);

export const deleteCustomerRequest = (payload: DeleteCustomerPayload) =>
  ({ type: CC.DELETE_REQUEST, payload } as const);

export const deleteCustomerSuccess = (customerId: string) =>
  ({ type: CC.DELETE_SUCCESS, payload: { customerId } } as const);

export const deleteCustomerFailure = (error: string) =>
  ({ type: CC.DELETE_FAILURE, payload: { error } } as const);

export type CateringCustomersAction =
  | ReturnType<typeof fetchCustomersRequest>
  | ReturnType<typeof fetchCustomersSuccess>
  | ReturnType<typeof fetchCustomersFailure>
  | ReturnType<typeof fetchCustomerDetailRequest>
  | ReturnType<typeof fetchCustomerDetailSuccess>
  | ReturnType<typeof fetchCustomerDetailFailure>
  | ReturnType<typeof updateCustomerRequest>
  | ReturnType<typeof updateCustomerSuccess>
  | ReturnType<typeof updateCustomerFailure>
  | ReturnType<typeof deleteCustomerRequest>
  | ReturnType<typeof deleteCustomerSuccess>
  | ReturnType<typeof deleteCustomerFailure>;

// ════════════════════════════════════════════════════════════════════════════════
// INVOICES
// ════════════════════════════════════════════════════════════════════════════════

export interface InvoiceLineItemDraft {
  id: string;
  /** Item name, kept separate from `description` (notes) so the API receives them as distinct fields. */
  name?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  /** Catalog item id, when this line came from a catalog item on the order. */
  itemId?: string;
}

export interface InvoiceMilestoneDraft {
  label: string;
  amount: string;    // string from form input
  dueDate: string;   // YYYY-MM-DD
}

export interface CreateInvoiceFormData {
  orderId: string;
  /**
   * The order's human-readable number ("CAT-ORD-…"), known to the form from the selected order. Not sent to the
   * server: it lets the optimistic invoice row show the order number straight away instead of the raw order id
   * until the list refetch brings the real row.
   */
  orderNumber?: string;
  customerEmail: string;
  lineItems: InvoiceLineItemDraft[];
  appliedCharges: AppliedCharge[];
  appliedTaxes: AppliedTax[];
  taxRate: number;
  taxExempt: boolean;
  gratuity: number;
  paymentSchedule: InvoiceMilestoneDraft[];
  notes: string;
  discountAmount?: number;
  // Pre-computed totals for optimistic local state
  subtotal: number;
  deliveryFee: number;
  serviceCharge: number;
  tax: number;
  total: number;
  sendImmediately?: boolean;
  channels?: ('email' | 'sms')[];
  phoneOverride?: string;
}

export interface NotifyInvoiceData {
  channels?: ('email' | 'sms')[];
  subjectOverride?: string;
  bodyOverride?: string;
  /** SMS body, when it differs from the email body. Falls back to bodyOverride. */
  smsBodyOverride?: string;
  phoneOverride?: string;
  customerEmail?: string;
}

export interface FetchInvoicesParams {
  locationId?: string;
  token?: string | null;
  status?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export const fetchInvoicesRequest = (payload: FetchInvoicesParams = {}) =>
  ({ type: CI.FETCH_REQUEST, payload } as const);

export const fetchInvoicesSuccess = (invoices: CateringInvoice[], locationId: string, page?: PageMeta) =>
  ({ type: CI.FETCH_SUCCESS, payload: { invoices, locationId, page } } as const);

export const fetchInvoicesFailure = (error: string) =>
  ({ type: CI.FETCH_FAILURE, payload: { error } } as const);

export const createInvoiceRequest = (data: CreateInvoiceFormData) =>
  ({ type: CI.CREATE_REQUEST, payload: { data } } as const);

export const createInvoiceSuccess = (invoice: CateringInvoice) =>
  ({ type: CI.CREATE_SUCCESS, payload: { invoice } } as const);

export const createInvoiceFailure = (error: string) =>
  ({ type: CI.CREATE_FAILURE, payload: { error } } as const);

export const updateInvoiceRequest = (id: string, data: Partial<CreateInvoiceFormData>) =>
  ({ type: CI.UPDATE_REQUEST, payload: { id, data } } as const);

export const updateInvoiceSuccess = (invoice: Partial<CateringInvoice> & { id: string }) =>
  ({ type: CI.UPDATE_SUCCESS, payload: { invoice } } as const);

export const updateInvoiceFailure = (error: string) =>
  ({ type: CI.UPDATE_FAILURE, payload: { error } } as const);

export const fetchInvoiceDetailRequest = (id: string) =>
  ({ type: CI.FETCH_DETAIL_REQUEST, payload: { id } } as const);

export const fetchInvoiceDetailSuccess = (invoice: CateringInvoice) =>
  ({ type: CI.FETCH_DETAIL_SUCCESS, payload: { invoice } } as const);

export const fetchInvoiceDetailFailure = (error: string) =>
  ({ type: CI.FETCH_DETAIL_FAILURE, payload: { error } } as const);

export const notifyInvoiceRequest = (id: string, data: NotifyInvoiceData = {}) =>
  ({ type: CI.NOTIFY_REQUEST, payload: { id, data } } as const);

export const notifyInvoiceSuccess = (
  invoiceId: string,
  result: SendOutcome & { status: string; issuedAt: string | null },
) => ({ type: CI.NOTIFY_SUCCESS, payload: { invoiceId, ...result } } as const);

export const notifyInvoiceFailure = (error: string) =>
  ({ type: CI.NOTIFY_FAILURE, payload: { error } } as const);

export const sendPaymentReminderRequest = (id: string) =>
  ({ type: CI.SEND_REMINDER_REQUEST, payload: { id } } as const);

export const sendPaymentReminderSuccess = (invoiceId: string, result: SendOutcome) =>
  ({ type: CI.SEND_REMINDER_SUCCESS, payload: { invoiceId, ...result } } as const);

export const sendPaymentReminderFailure = (error: string) =>
  ({ type: CI.SEND_REMINDER_FAILURE, payload: { error } } as const);

export const statusActionRequest = (id: string, action: 'void' | 'mark_paid') =>
  ({ type: CI.STATUS_ACTION_REQUEST, payload: { id, action } } as const);

export const statusActionSuccess = (invoiceId: string, status: string, updatedAt: string) =>
  ({ type: CI.STATUS_ACTION_SUCCESS, payload: { invoiceId, status, updatedAt } } as const);

export const statusActionFailure = (error: string) =>
  ({ type: CI.STATUS_ACTION_FAILURE, payload: { error } } as const);

// ── Record payment / refund (server-backed) ────────────────────────────────
export interface RecordPaymentInput {
  type: 'PAYMENT' | 'REFUND';
  amount: number;                 // positive magnitude
  method?: string;
  reference?: string;
  reason?: RefundReason | string; // required for REFUND
  paymentScheduleId?: string;     // for REFUND: the schedule row (milestone) being reversed
  notes?: string;
  paymentDate?: string;
}

export const recordPaymentRequest = (invoiceId: string, input: RecordPaymentInput) =>
  ({ type: CI.RECORD_PAYMENT_REQUEST, payload: { invoiceId, input } } as const);

export const recordPaymentSuccess = (
  invoiceId: string,
  payment: Payment,
  status: string,
  amountPaid: number,
  outstandingBalance: number,
  updatedAt: string,
) =>
  ({
    type: CI.RECORD_PAYMENT_SUCCESS,
    payload: { invoiceId, payment, status, amountPaid, outstandingBalance, updatedAt },
  } as const);

export const recordPaymentFailure = (error: string) =>
  ({ type: CI.RECORD_PAYMENT_FAILURE, payload: { error } } as const);

export const addPaymentLocal = (invoiceId: string, payment: Omit<Payment, 'id'>) =>
  ({ type: CI.ADD_PAYMENT_LOCAL, payload: { invoiceId, payment } } as const);

export const issueRefundLocal = (invoiceId: string, amount: number, reason: RefundReason, notes: string) =>
  ({ type: CI.ISSUE_REFUND_LOCAL, payload: { invoiceId, amount, reason, notes } } as const);

export const addCommLogLocal = (invoiceId: string, entry: Omit<CommunicationLogEntry, 'id'>) =>
  ({ type: CI.ADD_COMM_LOG_LOCAL, payload: { invoiceId, entry } } as const);

export type CateringInvoicesAction =
  | ReturnType<typeof fetchInvoicesRequest>
  | ReturnType<typeof fetchInvoicesSuccess>
  | ReturnType<typeof fetchInvoicesFailure>
  | ReturnType<typeof createInvoiceRequest>
  | ReturnType<typeof createInvoiceSuccess>
  | ReturnType<typeof createInvoiceFailure>
  | ReturnType<typeof updateInvoiceRequest>
  | ReturnType<typeof updateInvoiceSuccess>
  | ReturnType<typeof updateInvoiceFailure>
  | ReturnType<typeof fetchInvoiceDetailRequest>
  | ReturnType<typeof fetchInvoiceDetailSuccess>
  | ReturnType<typeof fetchInvoiceDetailFailure>
  | ReturnType<typeof notifyInvoiceRequest>
  | ReturnType<typeof notifyInvoiceSuccess>
  | ReturnType<typeof notifyInvoiceFailure>
  | ReturnType<typeof sendPaymentReminderRequest>
  | ReturnType<typeof sendPaymentReminderSuccess>
  | ReturnType<typeof sendPaymentReminderFailure>
  | ReturnType<typeof statusActionRequest>
  | ReturnType<typeof statusActionSuccess>
  | ReturnType<typeof statusActionFailure>
  | ReturnType<typeof recordPaymentRequest>
  | ReturnType<typeof recordPaymentSuccess>
  | ReturnType<typeof recordPaymentFailure>
  | ReturnType<typeof addPaymentLocal>
  | ReturnType<typeof issueRefundLocal>
  | ReturnType<typeof addCommLogLocal>;

// ════════════════════════════════════════════════════════════════════════════════
// ORDER TYPES
// ════════════════════════════════════════════════════════════════════════════════

export const fetchOrderTypesRequest = (locationId?: string) =>
  ({ type: COT.FETCH_REQUEST, payload: { locationId } } as const);

export const fetchOrderTypesSuccess = (orderTypes: CateringOrderType[], locationId: string) =>
  ({ type: COT.FETCH_SUCCESS, payload: { orderTypes, locationId } } as const);

export const fetchOrderTypesFailure = (error: string) =>
  ({ type: COT.FETCH_FAILURE, payload: { error } } as const);

export const fetchOrderTypeFeesRequest = (orderTypeId: string, locationId?: string) =>
  ({ type: COT.FETCH_FEES_REQUEST, payload: { orderTypeId, locationId } } as const);

export const fetchOrderTypeFeesSuccess = (fees: CateringOrderTypeFeeRedux[], orderTypeId: string) =>
  ({ type: COT.FETCH_FEES_SUCCESS, payload: { fees, orderTypeId } } as const);

export const fetchOrderTypeFeesFailure = (error: string) =>
  ({ type: COT.FETCH_FEES_FAILURE, payload: { error } } as const);

// ════════════════════════════════════════════════════════════════════════════════
// SETTINGS
// ════════════════════════════════════════════════════════════════════════════════

export const fetchSettingRequest = (key: string, locationId?: string) =>
  ({ type: CS.FETCH_REQUEST, payload: { key, locationId } } as const);

export const fetchMultipleSettingsRequest = (locationId?: string) =>
  ({ type: CS.FETCH_BULK_REQUEST, payload: { locationId } } as const);

export const fetchMultipleSettingsSuccess = (settings: Record<string, string | null>) =>
  ({ type: CS.FETCH_BULK_SUCCESS, payload: { settings } } as const);

export const fetchMultipleSettingsFailure = (error: string) =>
  ({ type: CS.FETCH_BULK_FAILURE, payload: { error } } as const);

export const fetchSettingSuccess = (key: string, value: string | null) =>
  ({ type: CS.FETCH_SUCCESS, payload: { key, value } } as const);

export const fetchSettingFailure = (error: string) =>
  ({ type: CS.FETCH_FAILURE, payload: { error } } as const);

export const upsertSettingRequest = (key: string, value: string, locationId?: string) =>
  ({ type: CS.UPSERT_REQUEST, payload: { key, value, locationId } } as const);

export const upsertSettingSuccess = (key: string, value: string) =>
  ({ type: CS.UPSERT_SUCCESS, payload: { key, value } } as const);

export const upsertSettingFailure = (error: string) =>
  ({ type: CS.UPSERT_FAILURE, payload: { error } } as const);

export const upsertMultipleSettingsRequest = (entries: Array<{ key: string; value: string }>, locationId?: string) =>
  ({ type: CS.UPSERT_BULK_REQUEST, payload: { entries, locationId } } as const);

export const upsertMultipleSettingsSuccess = (entries: Array<{ key: string; value: string }>) =>
  ({ type: CS.UPSERT_BULK_SUCCESS, payload: { entries } } as const);

export const upsertMultipleSettingsFailure = (error: string) =>
  ({ type: CS.UPSERT_BULK_FAILURE, payload: { error } } as const);

// ════════════════════════════════════════════════════════════════════════════════
// COMMUNICATION TEMPLATES
// ════════════════════════════════════════════════════════════════════════════════

export const fetchCommTemplatesRequest = (payload: FetchCommTemplatesPayload = {}) =>
  ({ type: CCT.FETCH_REQUEST, payload } as const);

export const fetchCommTemplatesSuccess = (templates: ApiCommTemplate[], locationId: string) =>
  ({ type: CCT.FETCH_SUCCESS, payload: { templates, locationId } } as const);

export const fetchCommTemplatesFailure = (error: string) =>
  ({ type: CCT.FETCH_FAILURE, payload: { error } } as const);

export const upsertCommTemplateRequest = (payload: UpsertCommTemplateRequestPayload) =>
  ({ type: CCT.UPSERT_REQUEST, payload } as const);

export const upsertCommTemplateSuccess = (templates: ApiCommTemplate[]) =>
  ({ type: CCT.UPSERT_SUCCESS, payload: { templates } } as const);

export const upsertCommTemplateFailure = (error: string, is404 = false) =>
  ({ type: CCT.UPSERT_FAILURE, payload: { error, is404 } } as const);

export const restoreCommTemplateRequest = (payload: RestoreCommTemplateRequestPayload) =>
  ({ type: CCT.RESTORE_REQUEST, payload } as const);

export const restoreCommTemplateSuccess = (templates: ApiCommTemplate[]) =>
  ({ type: CCT.RESTORE_SUCCESS, payload: { templates } } as const);

export const restoreCommTemplateFailure = (error: string) =>
  ({ type: CCT.RESTORE_FAILURE, payload: { error } } as const);

export const fetchTemplateTagsRequest = (eventType: string, token?: string | null) =>
  ({ type: CCT.FETCH_TAGS_REQUEST, payload: { eventType, token } } as const);

export const fetchTemplateTagsSuccess = (eventType: string, tags: TemplateTag[]) =>
  ({ type: CCT.FETCH_TAGS_SUCCESS, payload: { eventType, tags } } as const);

export const fetchTemplateTagsFailure = (eventType: string, error: string) =>
  ({ type: CCT.FETCH_TAGS_FAILURE, payload: { eventType, error } } as const);

export type CateringCommTemplatesAction =
  | ReturnType<typeof fetchCommTemplatesRequest>
  | ReturnType<typeof fetchCommTemplatesSuccess>
  | ReturnType<typeof fetchCommTemplatesFailure>
  | ReturnType<typeof upsertCommTemplateRequest>
  | ReturnType<typeof upsertCommTemplateSuccess>
  | ReturnType<typeof upsertCommTemplateFailure>
  | ReturnType<typeof restoreCommTemplateRequest>
  | ReturnType<typeof restoreCommTemplateSuccess>
  | ReturnType<typeof restoreCommTemplateFailure>
  | ReturnType<typeof fetchTemplateTagsRequest>
  | ReturnType<typeof fetchTemplateTagsSuccess>
  | ReturnType<typeof fetchTemplateTagsFailure>;

// ════════════════════════════════════════════════════════════════════════════════
// RESOLVED NOTIFICATION TEMPLATE
// ════════════════════════════════════════════════════════════════════════════════

export const fetchResolvedTemplateRequest = (payload: FetchResolvedTemplatePayload) =>
  ({ type: CRT.FETCH_REQUEST, payload } as const);

export const fetchResolvedTemplateSuccess = (data: ResolvedNotificationTemplate, key: string) =>
  ({ type: CRT.FETCH_SUCCESS, payload: { data, key } } as const);

export const fetchResolvedTemplateFailure = (error: string, notConfigured: boolean, key: string) =>
  ({ type: CRT.FETCH_FAILURE, payload: { error, notConfigured, key } } as const);

export const resetResolvedTemplate = () => ({ type: CRT.RESET } as const);

export type CateringResolvedTemplateAction =
  | ReturnType<typeof fetchResolvedTemplateRequest>
  | ReturnType<typeof fetchResolvedTemplateSuccess>
  | ReturnType<typeof fetchResolvedTemplateFailure>
  | ReturnType<typeof resetResolvedTemplate>;

// ════════════════════════════════════════════════════════════════════════════════
// TAXES
// ════════════════════════════════════════════════════════════════════════════════

export const fetchTaxesRequest = (orderTypeId: string): FetchTaxesRequestAction => ({
  type: CT.FETCH_REQUEST,
  payload: { orderTypeId },
});

export const fetchTaxesSuccess = (
  taxes: TaxItem[],
  charges: TaxItem[],
  loadedKey: string,
): FetchTaxesSuccessAction => ({
  type: CT.FETCH_SUCCESS,
  payload: { taxes, charges, loadedKey },
});

export const fetchTaxesFailure = (error: string): FetchTaxesFailureAction => ({
  type: CT.FETCH_FAILURE,
  payload: error,
});

export const cateringLocationChanged = (locationId: string) => ({
  type: CATERING_LOCATION_CHANGED,
  payload: locationId,
} as const);
